import React, { useCallback, useContext, useEffect, useReducer, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Card,
  CardContent,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import SecurityIcon from "@material-ui/icons/Security";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SyncIcon from "@material-ui/icons/Sync";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ForbiddenPage from "../../components/ForbiddenPage";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const reducer = (state, action) => {
  if (action.type === "LOAD_LOGS") {
    const logs = action.payload;
    const newLogs = [];

    logs.forEach((log) => {
      const logIndex = state.findIndex((item) => item.id === log.id);
      if (logIndex !== -1) {
        state[logIndex] = log;
      } else {
        newLogs.push(log);
      }
    });

    return [...state, ...newLogs];
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles
  },
  filtersGrid: {
    marginBottom: theme.spacing(2)
  },
  statCard: {
    height: "100%",
    borderRadius: 12,
    boxShadow: "rgba(15, 23, 42, 0.08) 0px 8px 24px"
  },
  statTitle: {
    fontSize: 13,
    color: "#64748b",
    fontWeight: 600,
    marginBottom: theme.spacing(1)
  },
  statValue: {
    fontSize: 28,
    fontWeight: 700,
    color: theme.palette.primary.main
  },
  detailsCell: {
    maxWidth: 260,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  },
  tableHead: {
    backgroundColor: "#f8fafc"
  }
}));

const EVENT_OPTIONS = [
  { value: "", label: "Todos os eventos" },
  { value: "login_success", label: "Login OK" },
  { value: "login_failed", label: "Login falhou" },
  { value: "refresh_token", label: "Refresh token" },
  { value: "auth_me", label: "Sessão validada" },
  { value: "logout", label: "Logout" },
  { value: "ticket_accepted", label: "Ticket aceito" },
  { value: "ticket_ignored", label: "Ticket ignorado" },
  { value: "ticket_deleted", label: "Ticket excluído" }
];

const AccessLogs = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [logs, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [event, setEvent] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [stats, setStats] = useState({
    total: 0,
    loginSuccess: 0,
    loginFailed: 0,
    refresh: 0,
    me: 0,
    logout: 0,
    ticketAccepted: 0,
    ticketIgnored: 0,
    ticketDeleted: 0
  });

  useEffect(() => {
    if (user && user.id && !user.super) {
      toast.error("Apenas super admins podem acessar o Log de Acessos.");
      history.push("/");
    }
  }, [history, user]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam, event, startDate, endDate]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);

    try {
      const { data } = await api.get("/access-logs", {
        params: {
          pageNumber,
          searchParam,
          event,
          startDate,
          endDate
        }
      });

      dispatch({ type: "LOAD_LOGS", payload: data.logs || [] });
      setHasMore(data.hasMore);
      setStats(
        data.stats || {
          total: 0,
          loginSuccess: 0,
          loginFailed: 0,
          refresh: 0,
          me: 0,
          logout: 0,
          ticketAccepted: 0,
          ticketIgnored: 0,
          ticketDeleted: 0
        }
      );
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, searchParam, event, startDate, endDate]);

  useEffect(() => {
    if (user && user.super) {
      const delayDebounceFn = setTimeout(() => {
        fetchLogs();
      }, 350);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [fetchLogs, user]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 120) < clientHeight) {
      loadMore();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    return new Date(dateString).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  };

  const getEventLabel = (eventName) => {
    switch (eventName) {
      case "login_success":
        return "Login OK";
      case "login_failed":
        return "Login falhou";
      case "refresh_token":
        return "Refresh token";
      case "auth_me":
        return "Sessão validada";
      case "logout":
        return "Logout";
      case "ticket_accepted":
        return "Ticket aceito";
      case "ticket_ignored":
        return "Ticket ignorado";
      case "ticket_deleted":
        return "Ticket excluído";
      default:
        return eventName;
    }
  };

  const getEventColor = (eventName) => {
    switch (eventName) {
      case "login_success":
        return "primary";
      case "login_failed":
        return "secondary";
      case "logout":
        return "default";
      case "refresh_token":
        return "primary";
      case "auth_me":
        return "default";
      case "ticket_accepted":
        return "primary";
      case "ticket_ignored":
        return "secondary";
      case "ticket_deleted":
        return "secondary";
      default:
        return "default";
    }
  };

  if (user && user.id && !user.super) {
    return <ForbiddenPage />;
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>Log de Acessos</Title>
      </MainHeader>

      <Grid container spacing={2} className={classes.filtersGrid}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar por usuário, email, empresa, IP, rota ou telefone"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            fullWidth
            variant="outlined"
            label="Evento"
            value={event}
            onChange={(e) => setEvent(e.target.value)}
          >
            {EVENT_OPTIONS.map((option) => (
              <MenuItem key={option.value || "all"} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            fullWidth
            type="date"
            variant="outlined"
            label="Data inicial"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="date"
            variant="outlined"
            label="Data final"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} style={{ marginBottom: 16 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statTitle}>Total</Typography>
              <Typography className={classes.statValue}>{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statTitle}>Login OK</Typography>
              <Typography className={classes.statValue}>{stats.loginSuccess}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statTitle}>Login falhou</Typography>
              <Typography className={classes.statValue}>{stats.loginFailed}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statTitle}>Refresh</Typography>
              <Typography className={classes.statValue}>{stats.refresh}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statTitle}>Sessão validada</Typography>
              <Typography className={classes.statValue}>{stats.me}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statTitle}>Logout</Typography>
              <Typography className={classes.statValue}>{stats.logout}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statTitle}>Tickets aceitos</Typography>
              <Typography className={classes.statValue}>{stats.ticketAccepted}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statTitle}>Tickets ignorados</Typography>
              <Typography className={classes.statValue}>{stats.ticketIgnored}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card className={classes.statCard}>
            <CardContent>
              <Typography className={classes.statTitle}>Tickets excluídos</Typography>
              <Typography className={classes.statValue}>{stats.ticketDeleted}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        <Table size="small">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Evento</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Empresa</TableCell>
              <TableCell>IP</TableCell>
              <TableCell>Método</TableCell>
              <TableCell>Rota</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id} hover>
                <TableCell>{formatDate(log.createdAt)}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={getEventColor(log.event)}
                    label={getEventLabel(log.event)}
                    icon={
                      log.event === "login_success" ? (
                        <SecurityIcon />
                      ) : log.event === "logout" ? (
                        <ExitToAppIcon />
                      ) : log.event === "refresh_token" ? (
                        <SyncIcon />
                      ) : log.event === "ticket_accepted" ? (
                        <CheckCircleIcon />
                      ) : log.event === "ticket_ignored" ? (
                        <BlockIcon />
                      ) : log.event === "ticket_deleted" ? (
                        <DeleteForeverIcon />
                      ) : (
                        <AccountCircleIcon />
                      )
                    }
                  />
                </TableCell>
                <TableCell>{log.userName || "-"}</TableCell>
                <TableCell>{log.userEmail || "-"}</TableCell>
                <TableCell>{log.companyName || "-"}</TableCell>
                <TableCell>{log.ip || "-"}</TableCell>
                <TableCell>{log.method || "-"}</TableCell>
                <TableCell>{log.requestPath || "-"}</TableCell>
                <TableCell>{log.statusCode || "-"}</TableCell>
                <TableCell className={classes.detailsCell}>
                  <Tooltip title={log.details || "-"} arrow>
                    <span>{log.details || "-"}</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton columns={10} />}
            {!loading && logs.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  Nenhum registro encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default AccessLogs;
